/**
 * EL TEXTO QUE SE LE ENSEÑA A UNA PERSONA CUANDO ALGO FALLA.
 *
 * En esta casa el error nunca es técnico: al usuario, una frase que entienda; el detalle,
 * a la consola. Pero por toda la app estaba escrito así:
 *
 *     toast.error(e.response?.data?.detail || 'No se pudo guardar')
 *
 * y eso da por hecho que `detail` es una cadena. Cuando el backend rechaza un dato por su
 * tipo, FastAPI NO manda una cadena: manda la lista de errores de validación, que son
 * objetos `{type, loc, msg, input, ctx, url}`. Ese array acaba dentro del aviso, React
 * intenta pintar un objeto como texto y **se cae la pantalla entera**. Le pasó al
 * cuestionario del alta: el cliente terminaba de contestar, pulsaba «Calcular mis macros»
 * y se quedaba mirando una pantalla en blanco con una traza.
 *
 * Aquí se devuelve SIEMPRE una cadena. Y si el error trae detalle técnico, se escribe en la
 * consola, que es donde sirve de algo.
 */
import { RANGOS_PERFIL } from './rangosDelPerfil';

// CÓMO SE LLAMAN LAS COSAS PARA QUIEN LAS ESCRIBE. El campo se llama `height` en la base y
// «tu altura» en la pantalla, y lo que hay que enseñarle es lo segundo: «Revisa el campo
// height» no le dice a nadie dónde tiene que volver. Y con el rango puesto, que es lo que de
// verdad le desatasca: sabe qué esperábamos.
//
// Y EL RANGO, DEL SITIO DONDE VIVE (9-09). Escrito a mano aquí se quedó viejo: decía «entre
// 3 y 70» de la grasa cuando el servidor ya solo aceptaba hasta 60, así que el aviso mandaba
// al cliente a escribir otro número que también se le iba a rechazar.
const COMO_SE_LLAMA = {
    height: ['tu altura', `en centímetros, entre ${RANGOS_PERFIL.height[0]} y ${RANGOS_PERFIL.height[1]}`],
    weight: ['tu peso', `en kilos, entre ${RANGOS_PERFIL.weight[0]} y ${RANGOS_PERFIL.weight[1]}`],
    body_fat: ['tu porcentaje de grasa', `entre ${RANGOS_PERFIL.body_fat[0]} y ${RANGOS_PERFIL.body_fat[1]}`],
    birthdate: ['tu fecha de nacimiento', null],
    email: ['tu email', null],
    phone: ['tu teléfono', null],
    goal: ['tu objetivo', null],
    peso_maximo: ['tu peso máximo', 'en kilos'],
    peso_minimo: ['tu peso más bajo', 'en kilos'],
    peso_mejor_momento: ['tu peso en tu mejor momento', 'en kilos'],
    peso: ['tu peso', null],
    porcentaje_graso: ['tu porcentaje de grasa', null],
};

// LOS MACROS, CON EL DÍA AL QUE PERTENECEN (27-09). Al guardar Mis macros con la grasa en
// 2000, el servidor rechaza `['body', 'training', 'fat']` y la pantalla decía «Revisa el
// dato «fat»»: el nombre interno, en inglés, delante del cliente. El `loc` trae también de
// qué día es el número, y eso es justo lo que necesita para encontrarlo.
const MACRO = {
    protein: ['las proteínas', 'g'],
    carbs: ['los hidratos', 'g'],
    fat: ['las grasas', 'g'],
    calories: ['las calorías', 'kcal'],
};
const DE_QUE_DIA = {
    training: 'del día de entreno',
    rest: 'del día de descanso',
    peri: 'del perientreno',
};

/** Cómo se llama para el cliente el dato de este `loc`, y en qué unidad va. */
function _comoSeLlama(loc) {
    const partes = Array.isArray(loc) ? loc.filter((p) => typeof p === 'string') : [];
    const campo = partes[partes.length - 1];
    const padre = partes[partes.length - 2];
    if (MACRO[campo]) {
        const [nombre, unidad] = MACRO[campo];
        return { nombre: DE_QUE_DIA[padre] ? `${nombre} ${DE_QUE_DIA[padre]}` : nombre, unidad, pista: null };
    }
    if (DE_QUE_DIA[campo]) return { nombre: `los macros ${DE_QUE_DIA[campo]}`, unidad: null, pista: null };
    const [nombre, pista] = COMO_SE_LLAMA[campo] || [null, null];
    return nombre ? { nombre, unidad: null, pista } : null;
}

// EL LÍMITE, DEL PROPIO RECHAZO. Pydantic manda en `ctx` el tope que se ha pasado (`le`,
// `ge`...). Sale del servidor en cada error, así que no se queda viejo como un rango
// escrito a mano aquí.
function _elLimite(error, unidad) {
    const ctx = error && error.ctx;
    if (!ctx || typeof ctx !== 'object') return null;
    const con = (n) => (unidad ? `${n} ${unidad}` : `${n}`);
    if (error.type === 'less_than_equal' && ctx.le != null) return `el máximo es ${con(ctx.le)}`;
    if (error.type === 'greater_than_equal' && ctx.ge != null) return `el mínimo es ${con(ctx.ge)}`;
    if (error.type === 'less_than' && ctx.lt != null) return `tiene que ser menos de ${con(ctx.lt)}`;
    if (error.type === 'greater_than' && ctx.gt != null) return `tiene que ser más de ${con(ctx.gt)}`;
    return null;
}

/**
 * LO QUE ESCRIBIMOS NOSOTROS SE ENSEÑA TAL CUAL.
 *
 * Cuando un validador nuestro rechaza un dato, escribe una frase en castellano y pensada
 * para el cliente: «Con esa fecha saldrían 0 años. Revísala: tiene que estar entre 14 y
 * 100». FastAPI la envuelve en `Value error, ...` y aquí se estaba tirando entera para
 * poner en su lugar «no hemos podido guardarlo así», que no dice qué pasa ni qué hacer.
 * Le costó a Jesús una tarde atascado en su perfil (9-09).
 *
 * Solo se rescata lo que lleva ese envoltorio: es la marca de que la frase es nuestra. Lo
 * que escribe Pydantic por su cuenta («Field required», «Input should be a valid number»)
 * viene en inglés y de eso no se le enseña ni una palabra.
 */
const _NUESTRA = /^Value error,\s*/;

/** Un aviso de validación de FastAPI, pasado a algo que se pueda leer. */
function _deLaValidacion(lista) {
    const primero = lista.find((e) => e && (e.msg || e.loc));
    if (!primero) return null;
    if (typeof primero.msg === 'string' && _NUESTRA.test(primero.msg)) {
        const nuestra = primero.msg.replace(_NUESTRA, '').trim();
        if (nuestra) return nuestra;
    }
    // `loc` es ['body', 'campo'] (o ['body', 'training', 'fat']): al usuario le vale saber
    // QUÉ dato no le hemos aceptado, dicho con sus palabras. Un campo que no sabemos
    // nombrar NO se enseña por su nombre interno: frase general.
    const dato = _comoSeLlama(primero.loc);
    if (!dato) return 'Hay algún dato que no hemos podido guardar. Revísalo y vuelve a intentarlo.';
    if (dato.pista) return `Revisa ${dato.nombre}: la esperamos ${dato.pista}.`;
    const limite = _elLimite(primero, dato.unidad);
    return limite
        ? `Revisa ${dato.nombre}: ${limite}.`
        : `Revisa ${dato.nombre}: no hemos podido guardar ese dato así.`;
}

/**
 * @param {unknown} error   lo que soltó axios (o un fetch, o un throw)
 * @param {string} porDefecto  qué decir cuando no se sabe nada más
 * @returns {string} siempre una cadena, nunca un objeto
 */
export function mensajeDeError(error, porDefecto = 'No se ha podido completar. Inténtalo otra vez.') {
    const detalle = error?.response?.data?.detail ?? error?.data?.detail ?? error?.detail;

    if (typeof detalle === 'string' && detalle.trim()) return detalle;

    if (Array.isArray(detalle)) {
        console.error('[error del servidor]', detalle);
        return _deLaValidacion(detalle) || porDefecto;
    }

    if (detalle && typeof detalle === 'object') {
        console.error('[error del servidor]', detalle);
        return typeof detalle.msg === 'string' ? detalle.msg : porDefecto;
    }

    // Sin respuesta del servidor: se ha caído la conexión, no es culpa de lo que escribió.
    if (error && error.request && !error.response) {
        console.error('[sin respuesta del servidor]', error.message || error);
        return 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.';
    }

    if (error) console.error('[error]', error);
    return porDefecto;
}

export default mensajeDeError;
